<template>
    <ul
        class="tabs"
        :class="classTabs"
    >
        <li
            v-for="tab in tabs"
            :key="tab.name"
            v-qa="tab.name"
            :class="getClassTab(tab)"
            class="tab"
        >
            <ThLink
                :to="tab.to"
                class="link"
            >
                {{ tab.label }}

                <span
                    v-if="tab.labelExtra"
                    class="extra"
                >
                    {{ tab.labelExtra }}
                </span>
            </ThLink>

            <LazyTooltip
                v-if="tab.tooltipPath"
                :path="tab.tooltipPath"
                :page-type="pageType"
            />
        </li>
    </ul>
</template>

<script generic="TName extends string" lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import type { TabType } from './Tabs'

import ThLink from '@/components/ui/buttons/ThLink.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

type Tab = TabType<TName>

const { currentTabResolver = undefined, selectorStyle = false } = defineProps<{
    tabs: Tab[]
    currentTabResolver?: (tab: Tab) => boolean
    selectorStyle?: boolean
    pageType: PageType
}>()

const router = useRouter()
const getClassTab = (tab: Tab) => {
    if (currentTabResolver) {
        return { 'current': currentTabResolver(tab) }
    }

    return {
        'current':
            router.resolve(tab.to).name === router.currentRoute.value.name,
    }
}

const classTabs = computed(() => ({
    'selector': selectorStyle,
}))
</script>

<style lang="scss" scoped>
.tabs {
    margin: 0 auto;
    width: 100%;
    max-width: 840px;
    display: flex;
    // padding: 4px;

    .tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 2px solid $grey-200;

        font-family: $effra-font-stack;
        line-height: 2rem;
        letter-spacing: 0.05em;
        font-size: 0.8rem;
        font-weight: 400;
        text-transform: uppercase;
        color: $slate;

        @include min-width(768) {
            font-size: 0.875rem;
        }

        &.current {
            border-bottom: 2px solid $slate;

            @include hover() {
                border-bottom: 2px solid $slate;
            }

            .link {
                pointer-events: none;
            }
        }

        @include hover() {
            border-bottom: 2px solid $grey-400;
        }

        .link {
            text-align: center;
            display: block;

            .extra {
                display: none;

                @include min-width(768) {
                    display: inline-block;
                }
            }
        }

        :deep(.tooltip-trigger) {
            position: relative;
            z-index: 2;

            &:after {
                margin-left: 4px;
            }
        }
    }

    &.selector {
        display: flex;
        align-items: center;
        height: 48px;
        border: 1px solid $grey-200;
        border-radius: 12px;

        .tab {
            height: 38px;
            border-radius: 10px;
            border-bottom: none;
            font-size: 11px !important;

            &.current {
                background-color: $blue;
                color: $white;
            }

            @include hover() {
                border-bottom: 0;
            }
        }
    }
}
</style>
